/**
 * Axis-aligned bounding box + sliding-collision math.
 *
 * Pure math layer — zero framework deps (no `three`, no `@react-three/*`, no React).
 * This keeps collision trivially unit-testable and lets the THREE bridge live in
 * useCollision.ts (the single seam between math and scene).
 *
 * Source: 03-RESEARCH.md Pattern 2 — standard per-axis sliding with Minkowski
 * inflation (shrink player to a point, inflate walls by playerRadius).
 */

export interface AABB {
  min: [number, number, number];
  max: [number, number, number];
}

export type Vec3 = [number, number, number];

/**
 * Point-inside-AABB test.
 *
 * `padding` expands the AABB outward (positive) or shrinks inward (negative):
 *   - positive: Minkowski inflation for collision (treat player as point, inflate walls)
 *   - negative: interior check (stay inside with margin — used for outerBoundary)
 *   - zero: exact inclusion
 *
 * Uses strict inequalities so an exact-edge point is treated as "outside" (padding=0)
 * or "inside" (padding>0) consistently. Matches 03-RESEARCH.md Pattern 2 behavior.
 */
export function pointInsideAABB(point: Vec3, aabb: AABB, padding = 0): boolean {
  return (
    point[0] > aabb.min[0] - padding && point[0] < aabb.max[0] + padding &&
    point[1] > aabb.min[1] - padding && point[1] < aabb.max[1] + padding &&
    point[2] > aabb.min[2] - padding && point[2] < aabb.max[2] + padding
  );
}

/**
 * Tests whether a 1D interval [aMin, aMax] would cross into an inflated AABB on
 * the given axis, using current Y/Z as the lateral coordinates.
 *
 * Returns the clamped axis value if a crossing occurs (stopping at the wall edge
 * minus playerRadius), or null if the movement is unobstructed.
 *
 * This is the swept-axis check that prevents tunneling — without it, large deltas
 * (large velocity × large delta) teleport straight through walls because the
 * endpoint lands on the far side where pointInsideAABB returns false.
 */
function sweepAxisX(
  currentX: number,
  proposedX: number,
  currentY: number,
  currentZ: number,
  walls: AABB[],
  playerRadius: number
): number {
  if (proposedX === currentX) return currentX;
  const movingPositive = proposedX > currentX;
  let clamped = proposedX;

  for (const w of walls) {
    // Wall must overlap the player's Y/Z extent (inflated) to matter
    if (
      currentY <= w.min[1] - playerRadius ||
      currentY >= w.max[1] + playerRadius ||
      currentZ <= w.min[2] - playerRadius ||
      currentZ >= w.max[2] + playerRadius
    ) {
      continue;
    }

    if (movingPositive) {
      // Moving east: wall blocks if currentX is west of wall AND proposedX reaches inflated west face
      const stopAt = w.min[0] - playerRadius;
      if (currentX < stopAt && proposedX >= stopAt) {
        clamped = Math.min(clamped, stopAt);
      }
    } else {
      // Moving west: wall blocks if currentX is east of wall AND proposedX reaches inflated east face
      const stopAt = w.max[0] + playerRadius;
      if (currentX > stopAt && proposedX <= stopAt) {
        clamped = Math.max(clamped, stopAt);
      }
    }
  }

  // If after clamping we'd still land inside any wall (e.g., starting inside), revert to current
  const test: Vec3 = [clamped, currentY, currentZ];
  if (walls.some((w) => pointInsideAABB(test, w, playerRadius))) {
    return currentX;
  }
  return clamped;
}

function sweepAxisZ(
  currentZ: number,
  proposedZ: number,
  currentX: number,
  currentY: number,
  walls: AABB[],
  playerRadius: number
): number {
  if (proposedZ === currentZ) return currentZ;
  const movingPositive = proposedZ > currentZ;
  let clamped = proposedZ;

  for (const w of walls) {
    if (
      currentY <= w.min[1] - playerRadius ||
      currentY >= w.max[1] + playerRadius ||
      currentX <= w.min[0] - playerRadius ||
      currentX >= w.max[0] + playerRadius
    ) {
      continue;
    }

    if (movingPositive) {
      const stopAt = w.min[2] - playerRadius;
      if (currentZ < stopAt && proposedZ >= stopAt) {
        clamped = Math.min(clamped, stopAt);
      }
    } else {
      const stopAt = w.max[2] + playerRadius;
      if (currentZ > stopAt && proposedZ <= stopAt) {
        clamped = Math.max(clamped, stopAt);
      }
    }
  }

  const test: Vec3 = [currentX, currentY, clamped];
  if (walls.some((w) => pointInsideAABB(test, w, playerRadius))) {
    return currentZ;
  }
  return clamped;
}

/**
 * Per-axis sliding collision resolver with swept-axis tunneling protection.
 *
 * Tests the proposed position axis-by-axis against every wall using a swept
 * interval check — if the current→proposed segment crosses a wall's inflated
 * face on an axis, the position is CLAMPED to (wall_face ± playerRadius) rather
 * than reverted to current. This prevents the tunneling bug where large deltas
 * (large velocity × tab-away delta) teleport the player through walls because
 * the endpoint lands on the far side and pointInsideAABB returns false.
 *
 * X is resolved first, then Z uses the already-resolved X — giving smooth "slide
 * along wall" feel when the player pushes into a corner.
 *
 * Y is NOT tested in Phase 3 (flat ground; caller locks Y to CAMERA_HEIGHT).
 * Phase 6 terrain raycast replaces the Y pass-through with ground height.
 *
 * @param current — authoritative camera position (last-resolved)
 * @param proposed — camera.position + velocity*delta
 * @param walls — all collidable AABBs from useNavStore
 * @param playerRadius — Minkowski inflation amount (default PLAYER_RADIUS = 0.4m)
 */
export function resolveMovement(
  current: Vec3,
  proposed: Vec3,
  walls: AABB[],
  playerRadius = 0.4
): Vec3 {
  const resolved: Vec3 = [current[0], current[1], current[2]];

  // X axis — swept check clamps to wall edge if we'd cross into one
  resolved[0] = sweepAxisX(
    current[0],
    proposed[0],
    current[1],
    current[2],
    walls,
    playerRadius
  );

  // Z axis — uses resolved X so the player can slide along a wall into a corner
  resolved[2] = sweepAxisZ(
    current[2],
    proposed[2],
    resolved[0],
    current[1],
    walls,
    playerRadius
  );

  // Y axis — Phase 3 flat ground; caller locks Y to CAMERA_HEIGHT so pass-through
  resolved[1] = proposed[1];

  return resolved;
}
